import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Points } from 'components/Points/Points';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import {
  AdminTrade,
  AdminTradeItem,
  EditableTradeItem,
  useLazyGetAdminTradeEditOptionsQuery,
  useAdminUpdateTradeItemMutation,
} from 'store/services/trade/tradeApi';
import { formatLanguage } from 'constants/languages';
import MagicButton from 'components/MagicButton/MagicButton';
import { toast } from 'react-toastify';

const formatCondition = (condition: string): string => {
  const conditionMap: Record<string, string> = {
    'NEAR_MINT': 'NM', 'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP', 'LIGHTLY_PLAYED': 'LP', 'LP': 'LP', 'SP': 'LP',
    'MODERATELY_PLAYED': 'MP', 'MP': 'MP',
    'HEAVILY_PLAYED': 'HP', 'HP': 'HP',
    'DAMAGED': 'DMG', 'DMG': 'DMG',
  };
  return conditionMap[condition] || condition;
};

const ALL_CONDITIONS = [
  { value: 'NEAR_MINT', label: 'NM - Near Mint' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP - Lightly Played' },
  { value: 'MODERATELY_PLAYED', label: 'MP - Moderately Played' },
];

interface EditValues {
  cardId: number;
  setCode: string;
  setName: string;
  condition: string;
  language: string;
  foil: boolean;
}

interface Props {
  trade: AdminTrade;
  item: AdminTradeItem;
  onClose: () => void;
}

export default function AdminEditTradeItemModal({ trade, item, onClose }: Props) {
  const [fetchEditOptions, { data: editOptionsData, isLoading: isLoadingOptions }] = useLazyGetAdminTradeEditOptionsQuery();
  const [adminUpdateItem, { isLoading: isSaving }] = useAdminUpdateTradeItemMutation();
  const [calculatePrice] = useCalculatePriceMutation();

  const [editValues, setEditValues] = useState<EditValues>({
    cardId: 0,
    setCode: item.setCode || '',
    setName: item.set,
    condition: item.condition,
    language: item.language,
    foil: item.foil,
  });

  const [editOption, setEditOption] = useState<EditableTradeItem | null>(null);
  const [previewPrice, setPreviewPrice] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Fetch edit options on mount
  useEffect(() => {
    fetchEditOptions({ tradeId: trade.id });
  }, [fetchEditOptions, trade.id]);

  // Once edit options load, find the matching item and set initial values
  useEffect(() => {
    if (editOptionsData?.items) {
      const matching = editOptionsData.items.find(opt => opt.itemId === item.id);
      if (matching) {
        setEditOption(matching);
        setEditValues({
          cardId: matching.cardId,
          setCode: item.setCode || '',
          setName: item.set,
          condition: matching.currentCondition,
          language: matching.currentLanguage,
          foil: matching.currentFoil,
        });
      }
    }
  }, [editOptionsData, item.id, item.setCode, item.set]);

  const originalPrice = item.pointValue;

  const hasChanges = editOption ? (
    editValues.cardId !== editOption.cardId ||
    editValues.condition !== editOption.currentCondition ||
    editValues.language !== editOption.currentLanguage ||
    editValues.foil !== editOption.currentFoil
  ) : false;

  const recalculatePrice = useCallback(async (values: EditValues) => {
    if (!editOption?.wantListId) return;
    setIsCalculating(true);
    try {
      const result = await calculatePrice({
        cards: [{
          wantListId: editOption.wantListId,
          cardId: values.cardId.toString(),
          condition: values.condition,
          language: values.language,
          foil: values.foil,
          set: values.setName,
        }]
      }).unwrap();
      const newPrice = result.cards.find((c: any) => c.wantListId === editOption.wantListId);
      if (newPrice) {
        setPreviewPrice(newPrice.displayPrice);
      }
    } catch {
      setPreviewPrice(null);
    } finally {
      setIsCalculating(false);
    }
  }, [calculatePrice, editOption?.wantListId]);

  useEffect(() => {
    if (hasChanges) {
      recalculatePrice(editValues);
    } else {
      setPreviewPrice(null);
    }
  }, [editValues, hasChanges, recalculatePrice]);

  const updateField = (updates: Partial<EditValues>) => {
    setEditValues(prev => ({ ...prev, ...updates }));
  };

  const handleSetChange = (selectedValue: string) => {
    const prefs = editOption?.preferences;
    if (!prefs) return;
    const selectedSetDetail = prefs.acceptableSetDetails.find(s => {
      const optionValue = s.cardId ? `${s.code}-${s.cardId}` : s.code;
      return optionValue === selectedValue;
    });
    if (!selectedSetDetail) return;

    const newAvailableInFoil = selectedSetDetail.availableInFoil ?? false;
    const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? true;

    const updates: Partial<EditValues> = {
      setCode: selectedSetDetail.code,
      setName: selectedSetDetail.name,
      cardId: selectedSetDetail.cardId,
    };

    const canOfferFoil = newAvailableInFoil && (prefs.acceptFoil ?? true);
    const canOfferNonFoil = newAvailableInNonFoil && (prefs.acceptNonFoil ?? true);

    if (editValues.foil && !canOfferFoil && canOfferNonFoil) {
      updates.foil = false;
    } else if (!editValues.foil && !canOfferNonFoil && canOfferFoil) {
      updates.foil = true;
    }

    updateField(updates);
  };

  const handleSave = async () => {
    try {
      const result = await adminUpdateItem({
        tradeId: trade.id,
        itemId: item.id,
        cardId: editValues.cardId,
        condition: editValues.condition,
        language: editValues.language,
        foil: editValues.foil,
      }).unwrap();

      if (result.totalDelta !== 0) {
        toast.success(`Item updated. Value changed by ${result.totalDelta > 0 ? '+' : ''}${result.totalDelta} pts.`);
      } else {
        toast.success('Item updated successfully.');
      }
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update item. Please try again.');
    }
  };

  const prefs = editOption?.preferences;

  const canOfferFoil = prefs ? (prefs.acceptFoil ?? true) : true;
  const canOfferNonFoil = prefs ? (prefs.acceptNonFoil ?? true) : true;
  const showFoilDropdown = canOfferFoil && canOfferNonFoil;

  const acceptableConditions = prefs?.acceptableConditions?.length
    ? ALL_CONDITIONS.filter(c => prefs.acceptableConditions.includes(c.value))
    : ALL_CONDITIONS;

  const currentSetValue = editValues.cardId
    ? `${editValues.setCode}-${editValues.cardId}`
    : editValues.setCode;

  const displayPrice = previewPrice ?? originalPrice;
  const perCardDelta = hasChanges && previewPrice !== null ? previewPrice - originalPrice : 0;
  const totalDelta = perCardDelta * item.quantity;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="admin-edit-trade-item-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-edit-modal-header">
          <h3 className="admin-edit-modal-title">Edit Trade Item (Admin)</h3>
          <button className="admin-edit-modal-close" onClick={onClose}>✕</button>
        </div>

        {isLoadingOptions ? (
          <div className="admin-edit-modal-loading">Loading edit options...</div>
        ) : !editOption ? (
          <div className="admin-edit-modal-loading">No edit options available for this item.</div>
        ) : (
          <>
            <div className="admin-edit-modal-content">
              {/* Card preview */}
              <div className="admin-edit-modal-card-preview">
                <img
                  src={AssetUrls.cards.getCardImage(item.scryfallId, 'art_crop')}
                  alt={item.name}
                  className="admin-edit-modal-card-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `${AssetUrls.base}/assets/card-back-default.png`;
                  }}
                />
                <div className="admin-edit-modal-card-info">
                  <span className="admin-edit-modal-card-name">{item.name}</span>
                  <span className="admin-edit-modal-card-detail">Trade #{trade.tradeNumber} · Qty: {item.quantity}</span>
                </div>
              </div>

              {/* Receiver's Accepted Criteria */}
              {prefs && (
                <div className="admin-edit-modal-criteria">
                  <label className="admin-edit-modal-criteria-label">Receiver's Accepted Criteria</label>
                  <div className="admin-edit-modal-criteria-badges">
                    {prefs.acceptableConditions?.length > 0 && (
                      <span className="criteria-badge">
                        Conditions: {prefs.acceptableConditions.map(formatCondition).join(', ')}
                      </span>
                    )}
                    {prefs.acceptableLanguages?.length > 0 && (
                      <span className="criteria-badge">
                        Languages: {prefs.acceptableLanguages.map(lang => formatLanguage(lang)).join(', ')}
                      </span>
                    )}
                    <span className="criteria-badge">
                      {prefs.acceptFoil && prefs.acceptNonFoil ? 'Foil & Non-Foil' : prefs.acceptFoil ? 'Foil Only' : 'Non-Foil Only'}
                    </span>
                    {prefs.acceptableSetDetails?.length > 0 && (
                      <span className="criteria-badge">
                        Sets: {prefs.acceptableSetDetails.map(s => s.name).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Edit fields */}
              <div className="admin-edit-modal-fields">
                {/* Set */}
                {prefs && prefs.acceptableSetDetails.length > 1 && (
                  <div className="admin-edit-modal-field">
                    <label className="admin-edit-modal-label">Set</label>
                    <select
                      value={currentSetValue}
                      onChange={(e) => handleSetChange(e.target.value)}
                      className="admin-edit-modal-select"
                      disabled={isSaving}
                    >
                      {[...prefs.acceptableSetDetails]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(setDetail => {
                          const uniqueValue = setDetail.cardId
                            ? `${setDetail.code}-${setDetail.cardId}`
                            : setDetail.code;
                          const displayText = setDetail.collectorNumber
                            ? `${setDetail.name} #${setDetail.collectorNumber}`
                            : setDetail.name;
                          return (
                            <option key={uniqueValue} value={uniqueValue}>{displayText}</option>
                          );
                        })}
                    </select>
                  </div>
                )}

                {/* Foil */}
                <div className="admin-edit-modal-field">
                  <label className="admin-edit-modal-label">Foil</label>
                  {showFoilDropdown ? (
                    <select
                      value={editValues.foil ? 'foil' : 'non-foil'}
                      onChange={(e) => updateField({ foil: e.target.value === 'foil' })}
                      className="admin-edit-modal-select"
                      disabled={isSaving}
                    >
                      <option value="non-foil">Non-Foil</option>
                      <option value="foil">Foil</option>
                    </select>
                  ) : (
                    <span className="admin-edit-modal-static">{canOfferFoil ? 'Foil' : 'Non-Foil'}</span>
                  )}
                </div>

                {/* Condition */}
                <div className="admin-edit-modal-field">
                  <label className="admin-edit-modal-label">Condition</label>
                  {acceptableConditions.length > 1 ? (
                    <select
                      value={editValues.condition}
                      onChange={(e) => updateField({ condition: e.target.value })}
                      className="admin-edit-modal-select"
                      disabled={isSaving}
                    >
                      {acceptableConditions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="admin-edit-modal-static">{formatCondition(editValues.condition)}</span>
                  )}
                </div>

                {/* Language */}
                <div className="admin-edit-modal-field">
                  <label className="admin-edit-modal-label">Language</label>
                  {prefs && prefs.acceptableLanguages?.length > 1 ? (
                    <select
                      value={editValues.language}
                      onChange={(e) => updateField({ language: e.target.value })}
                      className="admin-edit-modal-select"
                      disabled={isSaving}
                    >
                      {prefs.acceptableLanguages.map(lang => (
                        <option key={lang} value={lang}>{formatLanguage(lang)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="admin-edit-modal-static">{formatLanguage(editValues.language)}</span>
                  )}
                </div>
              </div>

              {/* Price summary */}
              <div className="admin-edit-modal-price-summary">
                <div className="admin-edit-modal-price-row">
                  <span className="admin-edit-modal-price-label">Current Price:</span>
                  <Points points={originalPrice * item.quantity} type="small" />
                </div>
                {hasChanges && previewPrice !== null && (
                  <>
                    <div className="admin-edit-modal-price-row">
                      <span className="admin-edit-modal-price-label">New Price:</span>
                      <Points points={displayPrice * item.quantity} type="small" />
                    </div>
                    {totalDelta !== 0 && (
                      <div className="admin-edit-modal-price-row">
                        <span className="admin-edit-modal-price-label">Change:</span>
                        <span className={`admin-edit-modal-price-delta ${totalDelta > 0 ? 'positive' : 'negative'}`}>
                          {totalDelta > 0 ? '+' : ''}{totalDelta} pts
                          {item.quantity > 1 && ` (${perCardDelta > 0 ? '+' : ''}${perCardDelta}/ea)`}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {isCalculating && (
                  <div className="admin-edit-modal-price-row">
                    <span className="admin-edit-modal-price-label">Calculating...</span>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-edit-modal-footer">
              <MagicButton
                label="Cancel"
                color="dark-grey"
                onPress={onClose}
                isDisabled={isSaving}
              />
              <MagicButton
                label={isSaving ? "Saving..." : "Save Changes"}
                color="purple"
                onPress={handleSave}
                isDisabled={isSaving || isCalculating || !hasChanges}
                isLoading={isSaving}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
